import { HEROES, PETS, MAX_HERO_SLOTS, BACKPACK_SLOTS, heroSlotCost, saveFree, type HeroSave } from '@knight/shared';
import { useEffect, useState } from 'preact/hooks';
import * as S from '../app/state';
import { t, tn } from '../i18n';
import { Btn, HeroPortrait, Icon, skillTip, itemTip, fmtTime } from './common';
import { PlayerLookup } from './Watch';
import { game } from '../game/client';
import { audio } from '../game/audio';
import { PetPortrait } from './Pets';
import { api } from '../net/api';
import { heroesView, deleteLocalHero, unlockedView } from '../app/heroes';
import { openIntro, withIntro } from '../app/hints';
import { KEY_LABELS } from '../game/input';

/** Consumes a pending deep link (realm code / avenge ghost) for the next join. */
function takePending(): { code?: string; ghost?: string } {
  const code = S.pendingRealm.value ?? undefined;
  const ghost = S.pendingAvenge.value ?? undefined;
  S.pendingRealm.value = null;
  S.pendingAvenge.value = null;
  return { code, ghost };
}

/** Starts a realm with the selected slot: resumes its hero or goes to the class picker. */
export function playSelectedSlot(): void {
  audio.unlock();
  const hv = heroesView();
  const slot = S.slotChoice.value;
  const save = hv.heroes.find((h) => h.slot === slot);
  if (save) {
    withIntro(() => {
      const p = takePending();
      game.join('realm', save.heroId, p.code, p.ghost, slot);
    });
  } else S.screen.value = 'heroes';
}

async function guarded(setBusy: (b: boolean) => void, fn: () => Promise<void>): Promise<void> {
  setBusy(true);
  try {
    await fn();
  } catch (e) {
    const key = (e as { key?: string }).key;
    S.showToast(key && t(key) !== key ? t(key) : t('toast.error', { msg: (e as Error).message }));
  } finally {
    setBusy(false);
  }
}

function HeroSlotCard(props: { slot: number; save: HeroSave | null; locked: boolean; selected: boolean; slots: number; compact?: boolean; onSelect: () => void }) {
  const { save, locked } = props;
  const online = S.conn.value === 'online';
  const [busy, setBusy] = useState(false);
  const acc = S.account.value;

  if (locked) {
    const cost = heroSlotCost(props.slots);
    return (
      <div class="slotcard locked">
        <div class="glyph">🔒</div>
        <b>{t('label.lockedSlot')}</b>
        <span class="small muted">{t('label.slot', { n: props.slot + 1 })}</span>
        <Btn small disabled={busy || !online || (acc?.fame ?? 0) < cost} onClick={() => void guarded(setBusy, async () => void (S.account.value = await api.heroSlotBuy()))}>
          {t('btn.unlockSlot', { cost })}
        </Btn>
        {!online && <span class="small muted">{t('toast.needServer')}</span>}
      </div>
    );
  }
  if (!save) {
    return (
      <button
        class={'slotcard empty' + (props.selected ? ' sel' : '')}
        onClick={() => {
          S.setSlot(props.slot);
          props.onSelect();
          if (!props.compact) S.screen.value = 'heroes';
        }}
      >
        <div class="glyph">+</div>
        <b>{t('btn.newHero')}</b>
        <span class="small muted">{t('label.slot', { n: props.slot + 1 })}</span>
      </button>
    );
  }
  const hero = HEROES[save.heroId];
  const points = saveFree(save);
  if (props.compact) {
    // inside the squad screen a card only picks the hero for the run; the leader starts it
    return (
      <div class={'slotcard compact' + (props.selected ? ' sel' : '')} onClick={props.onSelect}>
        <div class="row" style={{ alignItems: 'center', gap: 10 }}>
          <HeroPortrait hero={save.heroId} size={48} />
          <div class="col grow" style={{ gap: 2 }}>
            <b class="hname" style={{ color: hero.accent, fontSize: 18 }}>
              {tn('hero', save.heroId)}
            </b>
            <span class="small muted">
              {t('label.rankShort')} {save.rank} · {t('label.slot', { n: props.slot + 1 })}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div class={'slotcard' + (props.selected ? ' sel' : '')} onClick={props.onSelect}>
      <div class="row" style={{ alignItems: 'center', gap: 10 }}>
        <HeroPortrait hero={save.heroId} size={76} />
        <div class="col grow" style={{ gap: 2 }}>
          <div class="row" style={{ gap: 6 }}>
            <b class="hname" style={{ color: hero.accent }}>
              {tn('hero', save.heroId)}
            </b>
            <span class="rkpill" title={t('label.rankBar')}>
              {t('label.rankShort')} {save.rank}
            </span>
            {points > 0 && (
              <span class="pts" title={t('label.pointsHint')}>
                +{points}
              </span>
            )}
          </div>
          <span class="small muted">
            {t('label.runs')} {save.runs} · {fmtTime(save.playedSec)}
          </span>
        </div>
        <span class="slot-no">{t('label.slot', { n: props.slot + 1 })}</span>
      </div>
      {!props.compact && (
        <div class="row wrap kit" style={{ gap: 4 }}>
          <Icon kind="item" id={save.equipment.weapon.def} size="sm" tip={itemTip({ uid: 0, def: save.equipment.weapon.def, rarity: save.equipment.weapon.rarity, affixes: save.equipment.weapon.affixes ?? [] })} />
          {save.actives.map((s) => (
            <Icon kind="skill" id={s.id} size="sm" tip={skillTip(s.id, undefined, s.aspects)} />
          ))}
          {save.autos.map((s) => (
            <Icon kind="skill" id={s.id} size="sm" tip={skillTip(s.id, undefined, s.aspects)} />
          ))}
          {save.traits.map((tr) => (
            <Icon kind="skill" id={tr.id} size="sm" level={tr.level} tip={skillTip(tr.id, tr.level)} dim />
          ))}
          <div class="grow" />
          {save.backpack.length > 0 && (
            <span class="small muted" title={t('label.backpack')}>
              🎒 {save.backpack.length}/{BACKPACK_SLOTS}
            </span>
          )}
        </div>
      )}
      <div class="row" style={{ gap: 6 }}>
        <Btn
          small
          primary={props.selected}
          disabled={busy}
          onClick={() => {
            S.setSlot(props.slot);
            playSelectedSlot();
          }}
        >
          ▶ {t('btn.toRealm')}
        </Btn>
        <Btn
          small
          disabled={busy}
          onClick={() => {
            S.setSlot(props.slot);
            S.buildSlot.value = props.slot;
            S.screen.value = 'build';
          }}
        >
          {t('btn.setup')}
          {points > 0 ? ` +${points}` : ''}
        </Btn>
        <div class="grow" />
        <Btn
          small
          ghost
          disabled={busy}
          title={t('btn.abandon')}
          onClick={() => {
            void (async () => {
              const ok = await S.confirmDialog({ title: t('btn.abandon'), text: t('confirm.abandon', { name: tn('hero', save.heroId), rank: save.rank }), ok: t('btn.abandon'), danger: true });
              if (!ok) return;
              if (online) await guarded(setBusy, async () => void (S.account.value = await api.heroDelete(save.slot)));
              else deleteLocalHero(save.slot);
            })();
          }}
        >
          ✕
        </Btn>
      </div>
    </div>
  );
}

/** The account's hero slots; selecting one makes it the hero for the next realm. */
export function HeroSlots(props: { compact?: boolean; onSelect?: (slot: number, save: HeroSave | null) => void }) {
  const hv = heroesView();
  if (S.slotChoice.value >= hv.slots) S.setSlot(0);
  const shown = Math.min(MAX_HERO_SLOTS, hv.slots + 1);
  return (
    <div class={'hero-slots' + (props.compact ? ' compact' : '')}>
      {Array.from({ length: shown }).map((_, i) => {
        const save = hv.heroes.find((h) => h.slot === i) ?? null;
        return (
          <HeroSlotCard
            slot={i}
            save={save}
            locked={i >= hv.slots}
            selected={S.slotChoice.value === i}
            slots={hv.slots}
            compact={props.compact}
            onSelect={() => {
              if (i >= hv.slots) return;
              S.setSlot(i);
              props.onSelect?.(i, save);
            }}
          />
        );
      })}
    </div>
  );
}

const CONTROLS: [string, string][] = [
  ['ctl.move', 'W A S D'],
  ['ctl.fire', 'LMB'],
  ['ctl.dash', KEY_LABELS.dash],
  ['ctl.skills', KEY_LABELS.skills.join(' ')],
  ['ctl.cons', '1 2 3 4'],
  ['ctl.pick', KEY_LABELS.picks.slice(0, 3).join(' ')],
  ['ctl.interact', KEY_LABELS.interact],
  ['ctl.build', KEY_LABELS.build],
  ['btn.exitRealm', KEY_LABELS.leave],
];

export function Home() {
  const acc = S.account.value;
  const online = S.conn.value === 'online';
  const petId = acc?.pets?.active && PETS[acc.pets.active] ? acc.pets.active : null;
  const petLevel = petId ? acc?.pets.owned[petId]?.level ?? 1 : 0;
  const hv = heroesView();
  const selected = hv.heroes.find((h) => h.slot === S.slotChoice.value) ?? null;
  // celebrate heroes that opened since the last visit
  const unlockedKey = unlockedView().join(',');
  useEffect(() => {
    try {
      const now = unlockedKey.split(',');
      const seen = JSON.parse(localStorage.getItem('knight.unlockSeen') || 'null') as string[] | null;
      if (Array.isArray(seen)) {
        const fresh = now.filter((h) => !seen.includes(h));
        if (fresh.length) S.showToast(t('toast.heroUnlocked', { name: fresh.map((h) => tn('hero', h)).join(', ') }));
      }
      localStorage.setItem('knight.unlockSeen', JSON.stringify(now));
    } catch {
      /* ignore */
    }
  }, [unlockedKey]);
  return (
    <div class="screen home">
      <div class="home-wrap">
        {S.offlineMode.value && <div class="strip warn">{t('app.offline')}</div>}
        <section class="home-main col">
          <div class="cta panel">
            <div class="col grow" style={{ gap: 2 }}>
              <span class="small muted">{selected ? t('label.nextRealm') : t('label.noHeroYet')}</span>
              <b class="cta-title">{selected ? `${tn('hero', selected.heroId)} · ${t('label.rankShort')} ${selected.rank}` : t('btn.newHero')}</b>
            </div>
            <Btn primary onClick={() => playSelectedSlot()}>
              {selected ? t('btn.play') : t('btn.chooseHero')}
            </Btn>
          </div>
          <div class="row wraphead">
            <h3>{t('label.heroes')}</h3>
            <div class="grow" />
            <span class="small muted">{t('label.heroesHintShort')}</span>
          </div>
          <HeroSlots />
          <div class="small muted">{t('label.slotsShop')}</div>
          <div class="row" style={{ marginTop: 6 }}>
            <Btn
              block
              disabled={!online}
              onClick={() => {
                audio.unlock();
                S.queue.value = 'searching';
                game.send({ t: 'duel.queue' });
              }}
            >
              ⚔ {t('btn.duel')}
            </Btn>
            <Btn
              block
              disabled={!online}
              onClick={() => {
                if (!S.squad.value) game.send({ t: 'squad.create' });
                S.screen.value = 'squad';
              }}
            >
              ♟ {t('btn.squad')} {S.squad.value ? `(${S.squad.value.members.length})` : ''}
            </Btn>
          </div>
        </section>
        <aside class="home-side col">
          <div class="side-box panel col">
            <div class="row">
              <h3>{t('label.pet')}</h3>
              <div class="grow" />
              <Btn small onClick={() => (S.screen.value = 'pets')}>
                {t('btn.pets')}
              </Btn>
            </div>
            <div class="row clickable" style={{ cursor: 'pointer' }} onClick={() => (S.screen.value = 'pets')}>
              {petId ? <PetPortrait pet={petId} size={36} /> : <span class="avatar">🐾</span>}
              <span class="small">{petId ? `${tn('pet', petId)} · ${t('label.lvl')} ${petLevel}` : t('label.none')}</span>
            </div>
          </div>
          <div class="side-box panel col">
            <h3>{t('label.watchTitle')}</h3>
            <div class="small muted">{t('label.watchHint')}</div>
            <PlayerLookup compact />
          </div>
          <div class="side-box panel col">
            <div class="row">
              <h3>{t('label.howToPlay')}</h3>
              <div class="grow" />
              <Btn small ghost onClick={() => openIntro(() => {})}>
                {t('btn.showIntro')}
              </Btn>
            </div>
            <div class="small muted">{t('intro.goal.text')}</div>
            <div class="controls small">
              {CONTROLS.map(([k, v]) => (
                <>
                  <span>{k === 'btn.exitRealm' ? t(k, { key: v }).replace(/\s*\(.*\)\s*$/, '') : t(k)}</span>
                  <span>{v}</span>
                </>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
